import { formatCredentialType } from "@/features/credentials/pages/list/lib/format-credential-type";
import { useCompetitionsListQuery } from "@/features/credentials/queries/use-competitions-list";
import { useCredentialsListQuery } from "@/features/credentials/queries/use-credentials-list";
import { useEntitiesListQuery, useMissingEntityNamesQuery } from "@/features/credentials/queries/use-entities-list";
import { useVenuesListQuery } from "@/features/credentials/queries/use-venues-list";
import { getCredentialInfoDialogBack } from "@/features/credentials/pages/info/lib/get-credential-info-dialog-back";
import { useCredentialInfoParams } from "@/features/credentials/pages/info/hooks/use-credential-info-params";
import {
  Badge,
  EmptyState,
  EntityList,
  Item,
  ItemContent,
  ItemTitle,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooterActions,
  ModalHeader,
  ModalTitle,
  ScrollArea,
} from "@cmde/shadcn";
import { Loader2Icon, MapPinIcon, TrophyIcon, UsersIcon, type LucideIcon } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

function AccessEntityList({
  title,
  icon: Icon,
  ids,
  namesById,
  isLoading,
  emptyLabel,
}: {
  title: string;
  icon: LucideIcon;
  ids: string[];
  namesById: Map<string, string>;
  isLoading: boolean;
  emptyLabel: string;
}) {
  const items = ids.map((id) => ({
    id,
    name: namesById.get(id) ?? id,
  }));

  const { t } = useTranslation();

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Icon className="text-muted-foreground size-4" />
          <h3 className="text-sm font-medium text-foreground">{title}</h3>
        </div>
        {!isLoading && ids.length > 0 ? (
          <Badge variant="secondary">{ids.length}</Badge>
        ) : null}
      </div>

      <ScrollArea className="h-56">
        <EntityList
          data={items}
          getKey={(item) => item.id}
          isLoading={isLoading}
          loadingContent={
            <div className="text-muted-foreground flex h-56 items-center justify-center gap-2 text-sm">
              <Loader2Icon className="size-4 animate-spin" />
            </div>
          }
          emptyContent={
            <div className="flex h-56 items-center">
              <EmptyState
                title={emptyLabel}
                description={t("credentials.info.no_items_found")}
              />
            </div>
          }
          renderItem={(item) => (
            <Item variant="default" className="border-l-6 border-l-primary text-foreground">
              <ItemContent>
                <ItemTitle>{item.name}</ItemTitle>
              </ItemContent>
            </Item>
          )}
        />
      </ScrollArea>
    </section>
  );
}

export default function CredentialInfoDialogPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { customerId, organizationId, sport, credentialId } = useCredentialInfoParams();
  const credentialsListQuery = useCredentialsListQuery({ sport, organizationId });

  const credential = credentialsListQuery.data?.find(
    (item) => String(item.credentialId) === String(credentialId),
  );

  const isTeamUser = credential?.credentialType === "ENTITY";
  const isVenueUser = credential?.credentialType === "VENUE";
  const needsLookups = isTeamUser || isVenueUser;

  const competitionsQuery = useCompetitionsListQuery({
    sport,
    organizationId,
    enabled: needsLookups,
  });
  const entitiesQuery = useEntitiesListQuery({
    sport,
    organizationId,
    seasonIds: [],
    enabled: isTeamUser,
  });
  const venuesQuery = useVenuesListQuery({
    sport,
    organizationId,
    enabled: isVenueUser,
  });

  const teamIds = useMemo(
    () => (isTeamUser ? (credential?.ids ?? []).map(String) : []),
    [credential?.ids, isTeamUser],
  );
  const competitionNamesById = useMemo(
    () => new Map((competitionsQuery.data ?? []).map((item) => [String(item.competitionId), item.name])),
    [competitionsQuery.data],
  );
  const listedTeamNamesById = useMemo(
    () =>
      new Map(
        (entitiesQuery.data ?? [])
          .filter((item) => item.name)
          .map((item) => [String(item.entityId), item.name]),
      ),
    [entitiesQuery.data],
  );
  const entitiesListSettled = !entitiesQuery.isLoading && !entitiesQuery.isPlaceholderData;
  const missingEntityNamesQuery = useMissingEntityNamesQuery({
    sport,
    organizationId,
    entityIds: teamIds,
    knownNamesById: listedTeamNamesById,
    enabled: isTeamUser && entitiesListSettled,
  });
  const teamNamesById = useMemo(() => {
    const namesById = new Map(listedTeamNamesById);
    for (const entity of missingEntityNamesQuery.data ?? []) {
      if (entity.entityId && entity.name) {
        namesById.set(String(entity.entityId), entity.name);
      }
    }
    return namesById;
  }, [listedTeamNamesById, missingEntityNamesQuery.data]);
  const venueNamesById = useMemo(
    () => new Map((venuesQuery.data ?? []).map((item) => [String(item.venueId), item.name])),
    [venuesQuery.data],
  );

  const closeDialog = () => {
    navigate(getCredentialInfoDialogBack({ customerId, organizationId, sport }));
  };

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) {
          closeDialog();
        }
      }}
    >
      <ModalContent size="xl">
        <ModalHeader>
          <ModalTitle>
            {credential
              ? t("credentials.info.title", { name: credential.name })
              : t("credentials.info.not_found_title")}
          </ModalTitle>
          <ModalDescription>
            {credential
              ? formatCredentialType(credential.credentialType, t)
              : t("credentials.info.not_found_description")}
          </ModalDescription>
        </ModalHeader>
        <ModalBody>
          {credentialsListQuery.isLoading ? (
            <div className="text-muted-foreground flex items-center gap-2 text-sm">
              <Loader2Icon className="size-4 animate-spin" />
              {t("credentials.info.loading")}
            </div>
          ) : credential && needsLookups ? (
            <div className="grid md:grid-cols-2 gap-6 pb-4">
              <AccessEntityList
                title={t("credentials.info.competitions")}
                icon={TrophyIcon}
                ids={(credential.competitions ?? []).map(String)}
                namesById={competitionNamesById}
                isLoading={competitionsQuery.isLoading}
                emptyLabel={t("credentials.info.all_competitions")}
              />
              {isTeamUser ? (
                <AccessEntityList
                  title={t("credentials.info.teams")}
                  icon={UsersIcon}
                  ids={teamIds}
                  namesById={teamNamesById}
                  isLoading={!entitiesListSettled || missingEntityNamesQuery.isLoading}
                  emptyLabel={t("credentials.info.no_teams")}
                />
              ) : null}
              {isVenueUser ? (
                <AccessEntityList
                  title={t("credentials.info.venues")}
                  icon={MapPinIcon}
                  ids={(credential.ids ?? []).map(String)}
                  namesById={venueNamesById}
                  isLoading={venuesQuery.isLoading}
                  emptyLabel={t("credentials.info.no_venues")}
                />
              ) : null}
            </div>
          ) : null}
        </ModalBody>
        <ModalFooterActions
          secondaryAction={{
            label: t("credentials.info.close"),
            onClick: closeDialog,
          }}
        />
      </ModalContent>
    </Modal>
  );
}
